import { OptionsObject, VariantType } from 'notistack';
import infoIcon from '/images/notification/info.svg';
import errorIcon from '/images/notification/error.svg';
import successIcon from '/images/notification/success.svg';
import tournamentBlindsIcon from '/images/notification/tournament-blinds.svg';
import tournamentEliminatedIcon from '/images/notification/tournament-eliminated.svg';
import tournamentPauseIcon from '/images/notification/tournament-pause.svg';
import tournamentResumeIcon from '/images/notification/tournament-resume.svg';
import tournamentSuccessIcon from '/images/notification/tournament-success.svg';
import tournamentTrophyIcon from '/images/notification/tournament-trophy.svg';
import tournamentInfoIcon from '/images/notification/tournament-info.svg';

export type NotificationType =
    | 'SYSTEM_INFO'
    | 'SYSTEM_SUCCESS'
    | 'SYSTEM_ERROR'
    | 'TOURNAMENT_WIN'
    | 'TOURNAMENT_ELIMINATED'
    | 'TOURNAMENT_SUCCESS'
    | 'TOURNAMENT_STARTED'
    | 'TOURNAMENT_FINISHED'
    | 'TOURNAMENT_PAUSED'
    | 'TOURNAMENT_RESUMED'
    | 'TOURNAMENT_BLIND_CHANGE'
    | 'TOURNAMENT_INFO';

export interface NotificationData {
    message: string;
    scope?: 'app' | 'room';
    type?: NotificationType | string;
    roomId?: string;
    tournamentName?: string;
    gameId?: string;
    isRegistrationConfirmation?: boolean;
    [key: string]: any;
}

export class NotificationTemplateUtil {
    private static readonly NOTIFICATION_VARIANTS: Record<string, VariantType> = {
        SYSTEM_INFO: 'info',
        SYSTEM_SUCCESS: 'success',
        SYSTEM_ERROR: 'error',
        TOURNAMENT_WIN: 'default',
        TOURNAMENT_ELIMINATED: 'default',
        TOURNAMENT_SUCCESS: 'default',
        TOURNAMENT_STARTED: 'default',
        TOURNAMENT_FINISHED: 'default',
        TOURNAMENT_PAUSED: 'default',
        TOURNAMENT_RESUMED: 'default',
        TOURNAMENT_BLIND_CHANGE: 'default',
        TOURNAMENT_INFO: 'default',
        TOURNAMENT_LIFECYCLE: 'default',
    };

    private static getIconForType(type: string): string {
        const iconMap: Record<string, string> = {
            'SYSTEM_INFO': infoIcon,
            'SYSTEM_ERROR': errorIcon,
            'SYSTEM_SUCCESS': successIcon,
            'TOURNAMENT_WIN': tournamentTrophyIcon,
            'TOURNAMENT_ELIMINATED': tournamentEliminatedIcon,
            'TOURNAMENT_SUCCESS': tournamentSuccessIcon,
            'TOURNAMENT_STARTED': tournamentTrophyIcon,
            'TOURNAMENT_FINISHED': tournamentTrophyIcon,
            'TOURNAMENT_PAUSED': tournamentPauseIcon,
            'TOURNAMENT_RESUMED': tournamentResumeIcon,
            'TOURNAMENT_BLIND_CHANGE': tournamentBlindsIcon,
            'TOURNAMENT_INFO': tournamentInfoIcon,
            'TOURNAMENT_LIFECYCLE': tournamentInfoIcon,
        };
        return iconMap[type] || infoIcon;
    }

    public static getSnackbarOptions(data: NotificationData, message?: string, closeSnackbar?: (key: any) => void): OptionsObject {
        const normalizedType = data?.type?.toUpperCase() || 'SYSTEM_INFO';
        const variant = this.NOTIFICATION_VARIANTS[normalizedType] || this.NOTIFICATION_VARIANTS.SYSTEM_INFO;
        const icon = this.getIconForType(normalizedType);

        return {
            variant,
            autoHideDuration: 5000,
            persist: false,
            preventDuplicate: true,
            content: message ? (key: any) => (
                <div className={`notistack-MuiContent notistack-MuiContent-${variant}`} style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={icon} alt={normalizedType} width={12} height={12} style={{ marginRight: '0.5rem' }} />
                    <span style={{ marginRight: 'auto' }}>{message}</span>
                    {closeSnackbar && (
                        <button
                            className="MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit MuiIconButton-sizeSmall"
                            style={{
                                padding: '4px',
                                marginLeft: '8px',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                minWidth: '24px',
                                height: '24px',
                            }}
                            onClick={() => closeSnackbar?.(key)}
                            aria-label="close"
                            type="button"
                        >
                            <svg
                                className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall"
                                style={{ width: '20px', height: '20px', fill: '#FFFFFF' }}
                                focusable="false"
                                aria-hidden="true"
                                viewBox="0 0 24 24"
                                data-testid="CloseIcon"
                            >
                                <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                            </svg>
                        </button>
                    )}
                </div>
            ) : undefined
        };
    }

    /**
     * Process and format notification message based on data
     */
    public static formatMessage(data: NotificationData): string {
        // If message is already formatted, return as-is
        if (data.message) {
            return data.message;
        }
        // Add custom formatting logic here if needed
        return data.message || 'Notification';
    }

    public static shouldShowNotification(
        data: NotificationData,
        _currentRoomId: string | null,
        joinedRoomIds: string[]
    ): boolean {
        // App-scoped notifications are always shown
        if (data.scope === 'app' || !data.scope) {
            return true;
        }

        // Room-scoped: always show if user is in that room (regardless of view mode or detached state)
        if (data.scope === 'room' && data.roomId) {
            const isInRoom = joinedRoomIds.includes(data.roomId);
            return isInRoom;
        }

        return true;
    }

    public static addRoomContext(
        message: string,
        data: NotificationData,
        isInTargetRoom: boolean,
        roomName?: string
    ): string {
        if (data.scope === 'room' && !isInTargetRoom && roomName) {
            return `[${roomName}] ${message}`;
        }

        return message;
    }
}
